import React, { createRef, useEffect } from 'react'

import type { ProductOptionsConfiguratorData } from '@vp/product-options'
import { AlertBox, AlertBoxDismissButton, Box, Column, GridContainer, Row, useScreenClass, Button, Link } from '@vp/swan'
import { useLocalizations } from '@vp/ubik-localizations-provider'

import { ConnectedQuantitySelectorWrapper } from './configuration/connectedQuantitySelector'
import { useAppState } from '../root/contexts/AppContext'
import { Preview } from '../preview'
import { useProject } from '../root/contexts/ProjectContext'
import { QuantityContextProvider } from './contexts/QuantityContext'
import { scrollToElement, scrollToElementWithDelay } from '../../utils/ScrollHelpers'
import { InventoryContextProvider } from '../root/contexts/InventoryContext'
import { SizeChartContextProvider } from './contexts/SizeChartContext'
import useSizeChart from '../../hooks/queries/useSizeChart'
import { ProductOptionsQuantitySliderWrapper } from './ProductOptionsQuantitySliderWrapper'
import { useFeatureFlag } from '../../hooks/useFeatureFlag'
import { po_rules_based_configurator, global_rules_based_configurator } from '../../domain/FeatureFlags'

type ProductQuantitySelectionProps = {
  configuratorData?: ProductOptionsConfiguratorData
}

const ProductQuantitySelection: React.FC<ProductQuantitySelectionProps> = ({ configuratorData }) => {
  const { t } = useLocalizations()
  const { isAppStateInitialized, culture } = useAppState()
  const { abortRemoveProject, confirmRemoveProject, isPendingRemoval, productKey, projectNumber, projectCount } = useProject()
  const screenClass = useScreenClass()
  const { data: sizeChart, isSuccess } = useSizeChart(culture, productKey)
  const useProductOptions = useFeatureFlag(po_rules_based_configurator) || useFeatureFlag(global_rules_based_configurator)

  const isLoading = !isAppStateInitialized
  const scrollToPendingRemovalEl = createRef<HTMLElement>()
  const scrollToProjectEl = createRef<HTMLElement>()

  useEffect(() => {
    if (isPendingRemoval && scrollToPendingRemovalEl && scrollToPendingRemovalEl.current) {
      scrollToElement(scrollToPendingRemovalEl.current, 'center')
    }
  }, [isPendingRemoval])

  useEffect(() => {
    if (projectCount && scrollToProjectEl && scrollToProjectEl.current) {
      // add delay to smooth the scrolling process
      // and wait for the first paint of the rendered virtual dom
      scrollToElementWithDelay(scrollToProjectEl.current, 300, 'start')
    }
  }, [projectCount])

  // The first column needs to be a fixed percentage so the PURCS preview works with multiple views. 27% is the largest the image can take up wihtout being longer (in height) than the content on the right.
  const previewWidth = screenClass === 'xl' ? '30%' : screenClass === 'xs' ? '100%' : '27%'

  if (isPendingRemoval) {
    return (
      <Box className="qsp-project-quantity" ref={scrollToPendingRemovalEl} mb="between-subsections">
        <AlertBox
          skin="info"
          onRequestDismiss={() => {
            confirmRemoveProject()
          }}
          toast
        >
          {t('projects.removed-toast-message')}{' '}
          <Link
            data-testid="qsp-remove-undo"
            render={(p) => (
              <Button skin="link" className={p.className} onClick={() => abortRemoveProject()}>
                {p.children}
              </Button>
            )}
          >
            {t('projects.undo-project-removed-link')}
          </Link>
          <AlertBoxDismissButton data-testid="qsp-remove-confirm" visuallyHiddenLabel={t('projects.visually-hidden-label-clear-toast-button')} />
        </AlertBox>
      </Box>
    )
  }

  return (
    <Box ref={projectNumber === projectCount ? scrollToProjectEl : null}>
      <InventoryContextProvider>
        <QuantityContextProvider>
          <SizeChartContextProvider sizeChart={sizeChart} isSizeChartLoaded={isSuccess}>
            <GridContainer>
              <Row sticky>
                <Column span={6} spanXs={12} px={5} sticky unsticky="xs" style={{ width: previewWidth }}>
                  <Preview />
                </Column>
                <Column span={6} spanXs={12} style={{ flex: 1 }}>
                  {!isLoading &&
                    (useProductOptions ? <ProductOptionsQuantitySliderWrapper configuratorData={configuratorData} /> : <ConnectedQuantitySelectorWrapper />)}
                </Column>
              </Row>
            </GridContainer>
          </SizeChartContextProvider>
        </QuantityContextProvider>
      </InventoryContextProvider>
    </Box>
  )
}

export default ProductQuantitySelection
